import { Button } from "@/components/ui/button";
import {
  Send,
  MessageCircle,
  Phone,
  Copy,
  Clock,
  ShieldCheck,
  Headphones,
} from "lucide-react";
import { toast } from "sonner";
import { LINKS } from "@/data/site";
import { useTranslation } from "@/i18n/LanguageContext";

export default function Contact() {
  const { t } = useTranslation();
  const copyPhone = () => {
    navigator.clipboard.writeText(LINKS.whatsappNumber);
    toast.success(t.common.copied);
  };

  const INFO_ICONS = [Clock, ShieldCheck, Headphones];

  return (
    <div data-testid="contact-page">
      {/* Banner */}
      <section className="relative overflow-hidden border-b border-orange-500/20">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-orange-500/25 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 ring-1 ring-orange-500/30 text-orange-200 text-[10px] tracking-[0.3em] uppercase">
            <Headphones className="w-3 h-3" /> {t.contactPage.tag}
          </div>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl tracking-wider">
            <span className="text-zinc-50">{t.contactPage.titleA}</span>
            <span className="text-orange-grad text-glow">{t.contactPage.titleB}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-zinc-400">
            {t.contactPage.desc}
          </p>
        </div>
      </section>

      {/* QR cards */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12 grid lg:grid-cols-2 gap-6">
        {/* WhatsApp */}
        <div
          data-testid="contact-whatsapp-card"
          className="relative rounded-3xl overflow-hidden border border-orange-500/25 bg-gradient-to-br from-[#0e1311] to-[#07070a] p-8"
        >
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row gap-8 items-start">
            <div className="rounded-2xl p-2 bg-[#0a0a0d] ring-1 ring-orange-500/20 floaty">
              <img
                src={LINKS.whatsappQR}
                alt="WhatsApp QR Code"
                width={260}
                height={260}
                className="rounded-xl block bg-black"
                data-testid="whatsapp-qr"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 text-emerald-300 text-xs tracking-[0.3em] uppercase">
                <MessageCircle className="w-4 h-4" /> {t.contactPage.whatsappEyebrow}
              </div>
              <h3 className="mt-3 font-display text-3xl tracking-wider text-zinc-50">
                {t.contactPage.whatsappTitle}
              </h3>
              <p className="mt-2 text-sm text-zinc-400">
                {t.contactPage.whatsappDesc}
              </p>

              <div className="mt-5 flex items-center gap-3 rounded-xl border border-orange-500/15 bg-white/[0.02] px-4 py-3">
                <Phone className="w-4 h-4 text-orange-300" />
                <div className="font-mono-u2 text-zinc-100 tracking-wider">
                  {LINKS.whatsappNumber}
                </div>
                <button
                  onClick={copyPhone}
                  data-testid="copy-phone-btn"
                  className="ml-auto inline-flex items-center gap-1.5 text-xs text-orange-300 hover:text-orange-200"
                >
                  <Copy className="w-3.5 h-3.5" /> {t.common.copy}
                </button>
              </div>

              <Button
                asChild
                data-testid="open-whatsapp-btn"
                className="mt-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
              >
                <a href={LINKS.whatsappLink} target="_blank" rel="noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> {t.contactPage.whatsappCta}
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Telegram */}
        <div
          data-testid="contact-telegram-card"
          className="relative rounded-3xl overflow-hidden border border-orange-500/25 bg-gradient-to-br from-[#0a1018] to-[#07070a] p-8"
        >
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row gap-8 items-start">
            <div className="rounded-2xl p-2 bg-[#0a0a0d] ring-1 ring-orange-500/20 floaty">
              <img
                src={LINKS.telegramQR}
                alt="Telegram Group QR Code"
                width={260}
                height={260}
                className="rounded-xl block bg-black"
                data-testid="telegram-qr"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 text-sky-300 text-xs tracking-[0.3em] uppercase">
                <Send className="w-4 h-4" /> {t.contactPage.telegramEyebrow}
              </div>
              <h3 className="mt-3 font-display text-3xl tracking-wider text-zinc-50">
                {t.contactPage.telegramTitle}
              </h3>
              <p className="mt-2 text-sm text-zinc-400">
                {t.contactPage.telegramDesc}
              </p>

              <div className="mt-5 rounded-xl border border-orange-500/15 bg-white/[0.02] px-4 py-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  {t.contactPage.groupLink}
                </div>
                <div className="font-mono-u2 text-orange-200 mt-1 break-all text-sm">
                  t.me/+JVLa3hDzSxk3NzI1
                </div>
              </div>

              <Button
                asChild
                data-testid="open-telegram-btn"
                className="mt-4 rounded-full bg-sky-500 hover:bg-sky-400 text-black font-semibold"
              >
                <a href={LINKS.telegram} target="_blank" rel="noreferrer">
                  <Send className="w-4 h-4 mr-2" /> {t.contactPage.telegramCta}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Info strip */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10 grid sm:grid-cols-3 gap-4 pb-20">
        {t.contactPage.info.map((b, i) => {
          const Icon = INFO_ICONS[i] || Clock;
          return (
            <div
              key={b.title}
              className="rounded-2xl border border-orange-500/15 bg-[#0b0b0f] p-6"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 ring-1 ring-orange-500/30 flex items-center justify-center text-orange-300">
                <Icon className="w-5 h-5" />
              </div>
              <h4 className="mt-4 font-display text-2xl tracking-wider text-zinc-50">
                {b.title}
              </h4>
              <p className="mt-2 text-sm text-zinc-400">{b.desc}</p>
            </div>
          );
        })}
      </section>
    </div>
  );
}
